import { bunja } from 'bunja';
import { endOfToday, startOfToday } from 'date-fns';
import { atom } from 'jotai';
import { atomWithMutation } from 'jotai-tanstack-query';

import { getCsvApi } from '@flipster/apis/v1/csv';
import type { CsvApiType, GetCsvParams } from '@flipster/apis/v1/csv/types';
import { ProductType } from '@flipster/apis/v2/history/types';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { RouterScope } from '@flipster/react/providers/route';
import { css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { dateToEpochString } from '@flipster/utils/datetime';

import { userRegulationBunja } from '../../shared/regulation';

export type Option<T = string> = { label: string; value: T };
export type PeriodPresetType = '1d' | '1w' | '1m' | '3m' | 'none';
export const PeriodPresetOptions: { value: PeriodPresetType; label: string }[] = [
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
  { value: '1m', label: '1 Month' },
  { value: '3m', label: '3 Month' },
];

export type CategoryType = 'perpetual' | 'spot' | 'transaction';
export type SubCategoryType =
  | 'position'
  | 'pnl'
  | 'funding'
  | 'trade'
  | 'profit-boost'
  | 'airdrop'
  | 'earn'
  | 'launchpool'
  | 'deposit'
  | 'withdrawal'
  | 'conversion'
  | 'referral-and-affiliate';

type CategoryItem = Option<CategoryType> & { subCategories: SubCategoryItem[]; hideOnSpotOnly?: boolean };
type SubCategoryItem = Option<SubCategoryType> & {
  siblings?: Option[];
  additional?: AdditionalInfo;
  hideOnSpotOnly?: boolean;
};

export type AdditionalInfo = {
  label: string;
  options: (
    | typeof SymbolOption
    | typeof IntervalOption
    | typeof EarnIntervalOption
    | { type: 'currency'; value: 'fromCurrency' | 'toCurrency' }
    | { type: 'earn-currency'; value: 'currency' }
    | { type: 'launchpool' }
    | { type: 'separator'; icon: React.ReactNode }
  )[];
};

interface AdditionalValues {
  symbol?: string;
  currency?: string;
  interval?: string;
  granularity?: 'daily' | 'monthly';
  launchpoolId?: string;
  fromCurrency?: string;
  toCurrency?: string;
  hideCanceledOrder?: boolean;
}

type AdditionalOption = {
  type: 'common' | 'symbol' | 'currency' | 'earn-currency' | 'launchpool' | 'separator';
  value?: keyof AdditionalValues;
  options?: Option[];
  icon?: React.ReactNode;
};

const SymbolOption = {
  type: 'symbol',
  value: 'symbol',
} satisfies AdditionalOption;

const IntervalOption = {
  type: 'common',
  value: 'interval',
  options: [
    { label: 'Daily', value: '1d' },
    { label: 'Hourly', value: '1h' },
  ],
} satisfies AdditionalOption;

const EarnIntervalOption = {
  type: 'common',
  value: 'granularity',
  options: [
    { label: 'Daily', value: 'daily' },
    { label: 'Monthly', value: 'monthly' },
  ],
} satisfies AdditionalOption;

const TradeAdditionalOption = [
  { label: 'Fill', value: 'fill' },
  { label: 'Order', value: 'order' },
];

export const HistoryCategories: Record<CategoryType, CategoryItem> = {
  perpetual: {
    label: 'Perpetual',
    value: 'perpetual',
    hideOnSpotOnly: true,
    subCategories: [
      { label: 'Position', value: 'position', additional: { label: 'Asset', options: [SymbolOption] } },
      { label: 'P&L', value: 'pnl', additional: { label: 'Option', options: [IntervalOption] } },
      { label: 'Funding', value: 'funding', additional: { label: 'Asset', options: [SymbolOption] } },
      {
        label: 'Trade',
        value: 'trade',
        siblings: TradeAdditionalOption,
        additional: { label: 'Asset', options: [SymbolOption] },
      },
      { label: 'Profit boost', value: 'profit-boost', additional: { label: 'Asset', options: [SymbolOption] } },
      { label: 'Airdrop', value: 'airdrop', additional: { label: 'Asset', options: [SymbolOption] } },
    ],
  },
  spot: {
    label: 'Spot',
    value: 'spot',
    subCategories: [
      { label: 'P&L', value: 'pnl', additional: { label: 'Option', options: [IntervalOption] } },
      {
        label: 'Trade',
        value: 'trade',
        siblings: TradeAdditionalOption,
        additional: { label: 'Asset', options: [SymbolOption] },
      },
    ],
  },
  transaction: {
    label: 'Transaction',
    value: 'transaction',
    subCategories: [
      {
        label: 'Earn',
        value: 'earn',
        hideOnSpotOnly: true,
        additional: { label: 'Option', options: [EarnIntervalOption, { type: 'earn-currency', value: 'currency' }] },
      },
      {
        label: 'Launchpool reward',
        value: 'launchpool',
        hideOnSpotOnly: true,
        additional: { label: 'Option', options: [{ type: 'launchpool' }] },
      },
      { label: 'Deposit', value: 'deposit' },
      { label: 'Withdrawal', value: 'withdrawal' },
      {
        label: 'Conversion',
        value: 'conversion',
        additional: {
          label: 'Asset',
          options: [
            { type: 'currency', value: 'fromCurrency' },
            {
              type: 'separator',
              icon: (
                <ArrowRightIcon
                  size={16}
                  color={token('colors.content.default.level-2')}
                  className={css({ transform: { base: 'rotate(90deg)', tablet: 'rotate(0deg)' } })}
                />
              ),
            },
            { type: 'currency', value: 'toCurrency' },
          ],
        },
      },
      { label: 'Referral & Affiliate', value: 'referral-and-affiliate', hideOnSpotOnly: true },
    ],
  },
};

export const historyExportState = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isSpotOnlyRegulationAtom } = bunja.use(userRegulationBunja);
  const periodPresetAtom = atom<PeriodPresetType>('1d');
  const periodAtom = atom<{ startDate: Date; endDate: Date }>({
    startDate: startOfToday(),
    endDate: endOfToday(),
  });

  const categoryDataAtom = atom((get) => {
    const isSpotOnly = get(isSpotOnlyRegulationAtom);

    if (!isSpotOnly) return HistoryCategories;

    // Exclude spot-only categories
    const filteredCategories = Object.entries(HistoryCategories).reduce(
      (acc, [key, category]) => {
        if (isSpotOnly && category.hideOnSpotOnly) return acc;

        const filteredSubCategories = category.subCategories.filter(
          (subCategory) => !(isSpotOnly && subCategory.hideOnSpotOnly),
        );

        if (filteredSubCategories.length > 0) {
          acc[key as CategoryType] = { ...category, subCategories: filteredSubCategories };
        }

        return acc;
      },
      {} as Record<CategoryType, CategoryItem>,
    );

    return filteredCategories;
  });

  const selectedCategoryAtom = atom<CategoryType, [CategoryType], void>(
    'perpetual',
    (get, set, newCategory: CategoryType) => {
      const categoryData = get(categoryDataAtom);
      const category = categoryData[newCategory];
      const subCategory = category.subCategories[0];

      set(selectedCategoryAtom, newCategory);
      set(selectedSubCategoryAtom, subCategory.value);
    },
  );

  const selectedSubCategoryAtom = atom<SubCategoryType, [SubCategoryType], void>(
    'position',
    (get, set, newSubCategory: SubCategoryType) => {
      const subCategoryItems = get(subCategoryItemsAtom);
      const subCategory = subCategoryItems.find((item) => item.value === newSubCategory);
      const defaultSibling = subCategory?.siblings?.[0]?.value ?? undefined;
      const defaultAdditionalValues =
        subCategory?.additional?.options.reduce(
          (acc, additionalOption) => {
            if ('options' in additionalOption) acc[additionalOption.value] = additionalOption.options?.[0]?.value;
            return acc;
          },
          {} as Record<string, string | number | undefined>,
        ) ?? {};

      set(selectedSubCategoryAtom, newSubCategory);
      set(selectedSiblingAtom, defaultSibling);
      set(additionalValuesAtom, defaultAdditionalValues);
    },
  );

  const selectedSiblingAtom = atom<string | undefined>(undefined);
  const additionalValuesAtom = atom<AdditionalValues>({});

  function setExportParams({
    category,
    subCategory,
    siblingCategory,
    additionalOption,
  }: {
    category: CategoryType;
    subCategory: SubCategoryType;
    siblingCategory?: string;
    additionalOption?: AdditionalValues;
  }) {
    store.set(selectedCategoryAtom, category);
    store.set(selectedSubCategoryAtom, subCategory);
    store.set(selectedSiblingAtom, siblingCategory);
    store.set(additionalValuesAtom, additionalOption ?? {});
  }

  const subCategoryItemsAtom = atom((get) => {
    const categoryData = get(categoryDataAtom);
    const selectedCategory = get(selectedCategoryAtom);
    return categoryData[selectedCategory]?.subCategories ?? [];
  });

  const siblingItemsAtom = atom((get) => {
    const subCategoryItems = get(subCategoryItemsAtom);
    const selectedSubCategory = get(selectedSubCategoryAtom);
    return subCategoryItems.find((item) => item.value === selectedSubCategory)?.siblings ?? [];
  });

  const additionalInfoAtom = atom((get) => {
    const subCategoryItems = get(subCategoryItemsAtom);
    const selectedSubCategory = get(selectedSubCategoryAtom);
    return subCategoryItems.find((item) => item.value === selectedSubCategory)?.additional ?? null;
  });

  bunja.effect(() => {
    return store.sub(isSpotOnlyRegulationAtom, () => {
      const isSpotOnly = store.get(isSpotOnlyRegulationAtom);
      if (isSpotOnly) {
        const categoryData = store.get(categoryDataAtom);
        const subCategoryItem = categoryData.spot.subCategories[0];
        const defaultAdditionalValues =
          subCategoryItem.additional?.options.reduce(
            (acc, additionalOption) => {
              if ('options' in additionalOption) acc[additionalOption.value] = additionalOption.options?.[0]?.value;
              return acc;
            },
            {} as Record<string, string | number | undefined>,
          ) ?? {};
        store.set(selectedCategoryAtom, 'spot');
        store.set(selectedSubCategoryAtom, subCategoryItem.value);
        store.set(additionalValuesAtom, defaultAdditionalValues);
      }
    });
  });

  return {
    categoryDataAtom,
    periodPresetAtom,
    periodAtom,
    selectedCategoryAtom,
    selectedSubCategoryAtom,
    selectedSiblingAtom,
    additionalValuesAtom,
    subCategoryItemsAtom,
    siblingItemsAtom,
    additionalInfoAtom,
    setExportParams,
  };
});
historyExportState.debugLabel = 'historyExportState';

type CsvRequestInfo<T extends CsvApiType = CsvApiType> = {
  type: T;
  params: GetCsvParams[T];
};

export const csvExportState = bunja(() => {
  const { periodAtom, selectedCategoryAtom, selectedSubCategoryAtom, additionalValuesAtom, selectedSiblingAtom } =
    bunja.use(historyExportState);
  const router = bunja.use(RouterScope);

  const csvRequestInfoAtom = atom((get): CsvRequestInfo | null => {
    const { startDate, endDate } = get(periodAtom);
    const category = get(selectedCategoryAtom);
    const subCategory = get(selectedSubCategoryAtom);
    const values = get(additionalValuesAtom);
    const siblingCategory = get(selectedSiblingAtom);
    const isPerpetual = category === 'perpetual';

    function createRequestInfo<T extends CsvApiType>(
      type: T,
      additionalParams: Omit<GetCsvParams[T], 'startTime' | 'endTime'>,
    ): CsvRequestInfo {
      return {
        type,
        params: { startTime: dateToEpochString(startDate), endTime: dateToEpochString(endDate), ...additionalParams },
      };
    }

    const { symbol, currency, fromCurrency, toCurrency, interval, granularity, launchpoolId, hideCanceledOrder } =
      values;

    // trade 케이스는 siblingCategory에 따라 분기
    if (subCategory === 'trade') {
      const productType = isPerpetual ? ProductType.Perpetual : ProductType.Spot;
      return siblingCategory === 'fill'
        ? createRequestInfo('execution', { symbol, productType })
        : createRequestInfo('order', { symbol, productType, hideCanceledOrder });
    }

    switch (subCategory) {
      case 'position':
        return createRequestInfo('position', { symbol, isAirdrop: false });
      case 'airdrop':
        return createRequestInfo('position', { symbol, isAirdrop: true });
      case 'pnl':
        return createRequestInfo('pnl', {
          interval: interval ?? '1h',
          historyType: isPerpetual ? 'PNL_HISTORY_TYPE_FUTURES' : 'PNL_HISTORY_TYPE_SPOT',
        });
      case 'funding':
        return createRequestInfo('funding', { symbol });
      case 'profit-boost':
        return createRequestInfo('profit-boost', { symbol });
      case 'earn':
        return createRequestInfo('earn', { currency, granularity: granularity! });
      case 'launchpool':
        return createRequestInfo('launchpool', { launchpoolId: launchpoolId! });
      case 'deposit':
        return createRequestInfo('wallet', { transferType: 'deposit' });
      case 'withdrawal':
        return createRequestInfo('wallet', { transferType: 'withdrawal' });
      case 'conversion':
        return createRequestInfo('conversion', { fromCurrency, toCurrency });
      case 'referral-and-affiliate':
        return createRequestInfo('reward', { rewardCategory: 'REFERRAL' });
    }
  });

  const sendEmailMutationAtom = atomWithMutation((get) => ({
    mutationKey: ['getCsvApi'],
    mutationFn: () => {
      const csvRequestInfo = get(csvRequestInfoAtom);
      if (!csvRequestInfo) throw new Error('Invalid CSV request info');
      return getCsvApi(csvRequestInfo.type, csvRequestInfo.params);
    },
    onSuccess: () => {
      router.back();
      snackbar.success({ message: "We'll send your history to your email shortly" });
    },
    onError: () => {
      snackbar.error({ message: 'Failed to send history, please try again' });
    },
  }));

  return {
    csvRequestInfoAtom,
    sendEmailMutationAtom,
  };
});
csvExportState.debugLabel = 'csvExportState';
